// ============================================
// 4. PortfolioAnalysis.js - Weekly Performance Analysis
// ============================================

import React, { useState } from 'react';
import { BarChart, Bar, LineChart, Line, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';

const PortfolioAnalysis = () => {
  const [selectedWeek, setSelectedWeek] = useState('2025-W31');

  // Weekly analysis data
  const weeklyData = {
    '2025-W31': {
      weekOf: 'July 29 - Aug 2, 2025',
      totalTrades: 7,
      winRate: 71.4,
      totalPnL: 1238.75,
      totalReturn: 5.82,
      avgHoldTime: 2.3,
      largestWin: 775.00,
      largestLoss: -201.00,
      trades: [
        { ticker: 'AAPL', strategy: 'Options Core', pnl: 775.00, catalyst: 'iPhone 16 Leak', outcome: 'Win' },
        { ticker: 'NVDA', strategy: 'Phoenix Scalp', pnl: 666.00, catalyst: 'AI Partnership', outcome: 'Win' },
        { ticker: 'BABA', strategy: 'Phoenix Scalp', pnl: 566.25, catalyst: 'Regulatory Relief', outcome: 'Win' },
        { ticker: 'META', strategy: 'Government Intel', pnl: 234.50, catalyst: 'FTC Approval', outcome: 'Win' },
        { ticker: 'MSFT', strategy: 'Government Intel', pnl: -367.50, catalyst: 'Cloud Contract', outcome: 'Loss' },
        { ticker: 'TSLA', strategy: 'Options Core', pnl: -201.00, catalyst: 'Cybertruck Update', outcome: 'Loss' },
        { ticker: 'AMZN', strategy: 'Phoenix Scalp', pnl: -435.50, catalyst: 'AWS Growth', outcome: 'Loss' }
      ],
      strategyBreakdown: {
        'Options Core': { trades: 2, winRate: 50, pnl: 574.00 },
        'Government Intel': { trades: 2, winRate: 50, pnl: -133.00 },
        'Phoenix Scalp': { trades: 3, winRate: 66.7, pnl: 796.75 }
      },
      catalystAnalysis: {
        'Product Announcements': { trades: 2, winRate: 100, avgReturn: 4.1 },
        'Government/Regulatory': { trades: 3, winRate: 33.3, avgReturn: -0.8 },
        'Technical Setups': { trades: 2, winRate: 66.7, avgReturn: 2.8 }
      },
      marketConditions: {
        vixAvg: 15.2,
        spyReturn: 2.1,
        fearGreedAvg: 58,
        regime: 'Bullish Momentum'
      },
      analysis: {
        whatWorked: [
          'Product announcement catalysts were extremely strong',
          'Phoenix scalp strategy outperformed significantly', 
          'Position sizing kept losses manageable',
          'Exit discipline prevented larger losses'
        ],
        whatDidntWork: [
          'Government intel trades struggled in risk-on environment',
          'TSLA catalyst was weaker than expected',
          'Portfolio heat got too high mid-week'
        ],
        adjustments: [
          'Reduce allocation to government intel in bull markets',
          'Increase position size on product announcement plays',
          'Better timing on tech entries (avoid rotation periods)',
          'Implement maximum portfolio heat warnings'
        ],
        keyLearnings: [
          'Bull markets favor growth catalysts over defensive plays',
          'Phoenix scalp is becoming most reliable strategy',
          'Need to be more selective with government intel timing'
        ]
      }
    },
    '2025-W30': {
      weekOf: 'July 22 - July 26, 2025',
      totalTrades: 5,
      winRate: 60.0,
      totalPnL: 445.25,
      totalReturn: 2.15,
      avgHoldTime: 3.1,
      largestWin: 567.50,
      largestLoss: -234.75,
      trades: [
        { ticker: 'GOOGL', strategy: 'Options Core', pnl: 567.50, catalyst: 'AI Revenue Beat', outcome: 'Win' },
        { ticker: 'DIS', strategy: 'Phoenix Scalp', pnl: 345.25, catalyst: 'Streaming Numbers', outcome: 'Win' },
        { ticker: 'NFLX', strategy: 'Government Intel', pnl: 267.25, catalyst: 'Content Regulation', outcome: 'Win' },
        { ticker: 'UBER', strategy: 'Phoenix Scalp', pnl: -234.75, catalyst: 'Regulatory News', outcome: 'Loss' },
        { ticker: 'SHOP', strategy: 'Options Core', pnl: -500.00, catalyst: 'E-commerce Slowdown', outcome: 'Loss' }
      ],
      strategyBreakdown: {
        'Options Core': { trades: 2, winRate: 50, pnl: 67.50 },
        'Government Intel': { trades: 1, winRate: 100, pnl: 267.25 },
        'Phoenix Scalp': { trades: 2, winRate: 50, pnl: 110.50 }
      },
      catalystAnalysis: {
        'Earnings/Fundamentals': { trades: 2, winRate: 50, avgReturn: 0.3 },
        'Regulatory/Government': { trades: 2, winRate: 50, avgReturn: 0.8 },
        'Technical Setups': { trades: 1, winRate: 100, avgReturn: 3.4 }
      },
      marketConditions: {
        vixAvg: 18.7,
        spyReturn: -0.3,
        fearGreedAvg: 42,
        regime: 'Neutral/Choppy'
      },
      analysis: {
        whatWorked: [
          'High conviction plays (GOOGL) delivered big returns',
          'Government intel timing was excellent',
          'Risk management prevented catastrophic losses'
        ],
        whatDidntWork: [
          'Choppy market hurt momentum strategies',
          'SHOP catalyst was misread completely',
          'Too many trades in uncertain market'
        ],
        adjustments: [
          'Reduce trade frequency in choppy markets',
          'Focus on highest conviction setups only',
          'Better fundamental analysis for earnings plays'
        ],
        keyLearnings: [
          'Market regime drives strategy success more than individual skill',
          'Quality over quantity is crucial in uncertain markets',
          'Government intel works well in neutral markets'
        ]
      }
    }
  };

  const currentWeek = weeklyData[selectedWeek];

  // Chart data preparation
  const strategyChart = Object.entries(currentWeek.strategyBreakdown).map(([strategy, data]) => ({
    strategy: strategy.replace(' ', '\n'),
    pnl: data.pnl,
    winRate: data.winRate,
    trades: data.trades
  }));

  const catalystChart = Object.entries(currentWeek.catalystAnalysis).map(([catalyst, data]) => ({
    catalyst: catalyst.replace('/', '/\n'),
    winRate: data.winRate,
    avgReturn: data.avgReturn,
    trades: data.trades
  }));

  const dailyPnL = [
    { day: 'Mon', pnl: 234.50 },
    { day: 'Tue', pnl: 775.00 },
    { day: 'Wed', pnl: -201.00 },
    { day: 'Thu', pnl: 666.00 },
    { day: 'Fri', pnl: -235.75 }
  ];

  // Helper Functions
  const formatCurrency = (value) => new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2
  }).format(value);

  const formatPercent = (value) => `${value > 0 ? '+' : ''}${value.toFixed(1)}%`;

  return (
    <div style={{
      position: 'relative',
      minHeight: '100vh',
      backgroundColor: '#1a2332',
      color: '#ffffff',
      fontFamily: 'Georgia, serif',
      padding: '24px'
    }}>
      {/* WATERMARK */}
      <div style={{
        position: 'fixed',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        opacity: '0.03',
        pointerEvents: 'none',
        zIndex: '1',
        width: '800px',
        height: '800px'
      }}>
        <img 
          src="/finance-watermark.jpg"
          alt="Financial Trading Watermark"
          style={{ width: '100%', height: '100%', objectFit: 'contain' }}
        />
      </div>

      <div style={{ position: 'relative', zIndex: '2', maxWidth: '1400px', margin: '0 auto' }}>
        {/* Header */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: '32px'
        }}>
          <div>
            <h1 style={{
              color: '#ffffff',
              fontSize: '32px',
              fontWeight: 'bold',
              margin: '0',
              fontFamily: 'Georgia, serif'
            }}>📊 Portfolio Analysis</h1>
            <p style={{
              color: '#a0a9c1',
              fontSize: '16px',
              margin: '8px 0 0 0',
              fontFamily: 'Georgia, serif'
            }}>Weekly performance review and strategic insights</p>
          </div>
        </div>

        {/* Week Selector */}
        <div style={{
          backgroundColor: '#202A44',
          padding: '20px',
          borderRadius: '8px',
          border: '1px solid #3a4a6b',
          marginBottom: '32px'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '20px', marginBottom: '16px' }}>
            <div>
              <label style={{ color: '#a0a9c1', fontSize: '14px', marginRight: '8px', fontFamily: 'Georgia, serif' }}>
                Select Week:
              </label>
              <select 
                value={selectedWeek}
                onChange={(e) => setSelectedWeek(e.target.value)}
                style={{
                  backgroundColor: '#1a2332',
                  color: '#ffffff',
                  border: '1px solid #3a4a6b',
                  borderRadius: '4px',
                  padding: '8px',
                  fontFamily: 'Georgia, serif'
                }}
              >
                <option value="2025-W31">Week of July 29 - Strong Performance</option>
                <option value="2025-W30">Week of July 22 - Mixed Results</option>
              </select>
            </div>
          </div>

          {/* Week Summary */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))',
            gap: '16px',
            backgroundColor: '#1a2332',
            padding: '16px',
            borderRadius: '8px'
          }}>
            <div>
              <div style={{ color: '#a0a9c1', fontSize: '12px', fontFamily: 'Georgia, serif' }}>Week Of</div>
              <div style={{ color: '#D4AF37', fontSize: '14px', fontWeight: 'bold', fontFamily: 'Georgia, serif' }}>
                {currentWeek.weekOf}
              </div>
            </div>
            <div>
              <div style={{ color: '#a0a9c1', fontSize: '12px', fontFamily: 'Georgia, serif' }}>Total Trades</div>
              <div style={{ color: '#ffffff', fontSize: '18px', fontWeight: 'bold', fontFamily: 'Georgia, serif' }}>
                {currentWeek.totalTrades}
              </div>
            </div>
            <div>
              <div style={{ color: '#a0a9c1', fontSize: '12px', fontFamily: 'Georgia, serif' }}>Win Rate</div>
              <div style={{
                color: currentWeek.winRate >= 60 ? '#00FF5F' : currentWeek.winRate >= 40 ? '#FFB800' : '#FF0000',
                fontSize: '18px',
                fontWeight: 'bold',
                fontFamily: 'Georgia, serif'
              }}>
                {currentWeek.winRate.toFixed(1)}%
              </div>
            </div>
            <div>
              <div style={{ color: '#a0a9c1', fontSize: '12px', fontFamily: 'Georgia, serif' }}>Total P&L</div>
              <div style={{
                color: currentWeek.totalPnL > 0 ? '#00FF5F' : '#FF0000',
                fontSize: '18px',
                fontWeight: 'bold',
                fontFamily: 'Georgia, serif'
              }}>
                {formatCurrency(currentWeek.totalPnL)}
              </div>
            </div>
            <div>
              <div style={{ color: '#a0a9c1', fontSize: '12px', fontFamily: 'Georgia, serif' }}>Portfolio Return</div>
              <div style={{
                color: currentWeek.totalReturn > 0 ? '#00FF5F' : '#FF0000',
                fontSize: '18px',
                fontWeight: 'bold',
                fontFamily: 'Georgia, serif'
              }}>
                {formatPercent(currentWeek.totalReturn)}
              </div>
            </div>
            <div>
              <div style={{ color: '#a0a9c1', fontSize: '12px', fontFamily: 'Georgia, serif' }}>Avg Hold Time</div>
              <div style={{ color: '#ffffff', fontSize: '16px', fontWeight: 'bold', fontFamily: 'Georgia, serif' }}>
                {currentWeek.avgHoldTime} days
              </div>
            </div>
          </div>
        </div>

        {/* Charts Section */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(400px, 1fr))',
          gap: '24px',
          marginBottom: '32px'
        }}>
          {/* Strategy Performance */}
          <div style={{
            backgroundColor: '#202A44',
            padding: '24px',
            borderRadius: '8px',
            border: '1px solid #3a4a6b'
          }}>
            <h3 style={{ color: '#D4AF37', margin: '0 0 16px 0', fontFamily: 'Georgia, serif' }}>
              Strategy Performance
            </h3>
            <div style={{ width: '100%', height: '250px' }}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={strategyChart}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#3a4a6b" />
                  <XAxis 
                    dataKey="strategy" 
                    stroke="#a0a9c1" 
                    style={{ fontFamily: 'Georgia, serif', fontSize: '10px' }}
                  />
                  <YAxis 
                    stroke="#a0a9c1" 
                    style={{ fontFamily: 'Georgia, serif', fontSize: '10px' }}
                  />
                  <Tooltip 
                    contentStyle={{
                      backgroundColor: '#2B3554',
                      border: '1px solid #3a4a6b',
                      borderRadius: '8px',
                      color: '#ffffff',
                      fontFamily: 'Georgia, serif'
                    }}
                  />
                  <Bar dataKey="pnl" fill="#D4AF37" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Daily P&L */}
          <div style={{
            backgroundColor: '#202A44',
            padding: '24px',
            borderRadius: '8px',
            border: '1px solid #3a4a6b'
          }}>
            <h3 style={{ color: '#D4AF37', margin: '0 0 16px 0', fontFamily: 'Georgia, serif' }}>
              Daily P&L Progression
            </h3>
            <div style={{ width: '100%', height: '250px' }}>
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={dailyPnL}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#3a4a6b" />
                  <XAxis 
                    dataKey="day" 
                    stroke="#a0a9c1" 
                    style={{ fontFamily: 'Georgia, serif', fontSize: '12px' }}
                  />
                  <YAxis 
                    stroke="#a0a9c1" 
                    style={{ fontFamily: 'Georgia, serif', fontSize: '12px' }}
                  />
                  <Tooltip 
                    contentStyle={{
                      backgroundColor: '#2B3554',
                      border: '1px solid #3a4a6b',
                      borderRadius: '8px',
                      color: '#ffffff',
                      fontFamily: 'Georgia, serif'
                    }}
                  />
                  <Line 
                    type="monotone" 
                    dataKey="pnl" 
                    stroke="#00FF5F" 
                    strokeWidth={3} 
                    dot={{ fill: '#00FF5F', strokeWidth: 2, r: 6 }}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>

        {/* Analysis Sections */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(400px, 1fr))',
          gap: '24px',
          marginBottom: '32px'
        }}>
          {/* What Worked */}
          <div style={{
            backgroundColor: '#202A44',
            padding: '24px',
            borderRadius: '8px',
            border: '1px solid #3a4a6b'
          }}>
            <h3 style={{ color: '#00FF5F', margin: '0 0 16px 0', fontFamily: 'Georgia, serif' }}>
              ✅ What Worked This Week
            </h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              {currentWeek.analysis.whatWorked.map((item, index) => (
                <div key={index} style={{
                  backgroundColor: '#1a2332',
                  padding: '12px',
                  borderRadius: '6px',
                  color: '#ffffff',
                  fontSize: '14px',
                  fontFamily: 'Georgia, serif'
                }}>
                  • {item}
                </div>
              ))}
            </div>
          </div>

          {/* What Didn't Work */}
          <div style={{
            backgroundColor: '#202A44',
            padding: '24px',
            borderRadius: '8px',
            border: '1px solid #3a4a6b'
          }}>
            <h3 style={{ color: '#FF0000', margin: '0 0 16px 0', fontFamily: 'Georgia, serif' }}>
              ❌ What Didn't Work
            </h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              {currentWeek.analysis.whatDidntWork.map((item, index) => (
                <div key={index} style={{
                  backgroundColor: '#1a2332',
                  padding: '12px',
                  borderRadius: '6px',
                  color: '#ffffff',
                  fontSize: '14px',
                  fontFamily: 'Georgia, serif'
                }}>
                  • {item}
                </div>
              ))}
            </div>
          </div>

          {/* Strategic Adjustments */}
          <div style={{
            backgroundColor: '#202A44',
            padding: '24px',
            borderRadius: '8px',
            border: '1px solid #3a4a6b'
          }}>
            <h3 style={{ color: '#FFB800', margin: '0 0 16px 0', fontFamily: 'Georgia, serif' }}>
              🔧 Strategic Adjustments
            </h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              {currentWeek.analysis.adjustments.map((item, index) => (
                <div key={index} style={{
                  backgroundColor: '#1a2332',
                  padding: '12px',
                  borderRadius: '6px',
                  color: '#ffffff',
                  fontSize: '14px',
                  fontFamily: 'Georgia, serif'
                }}>
                  • {item}
                </div>
              ))}
            </div>
          </div>

          {/* Key Learnings */}
          <div style={{
            backgroundColor: '#202A44',
            padding: '24px',
            borderRadius: '8px',
            border: '1px solid #3a4a6b'
          }}>
            <h3 style={{ color: '#D4AF37', margin: '0 0 16px 0', fontFamily: 'Georgia, serif' }}>
              💡 Key Learnings
            </h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              {currentWeek.analysis.keyLearnings.map((item, index) => (
                <div key={index} style={{
                  backgroundColor: '#1a2332',
                  padding: '12px',
                  borderRadius: '6px',
                  color: '#ffffff',
                  fontSize: '14px',
                  fontFamily: 'Georgia, serif',
                  fontWeight: '600'
                }}>
                  🔸 {item}
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Trade Breakdown Table */}
        <div style={{
          backgroundColor: '#202A44',
          padding: '24px',
          borderRadius: '8px',
          border: '1px solid #3a4a6b'
        }}>
          <h3 style={{ color: '#D4AF37', margin: '0 0 16px 0', fontFamily: 'Georgia, serif' }}>
            📋 Complete Trade Breakdown
          </h3>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ borderBottom: '1px solid #3a4a6b' }}>
                  <th style={{ padding: '12px', textAlign: 'left', color: '#a0a9c1', fontFamily: 'Georgia, serif' }}>Ticker</th>
                  <th style={{ padding: '12px', textAlign: 'left', color: '#a0a9c1', fontFamily: 'Georgia, serif' }}>Strategy</th>
                  <th style={{ padding: '12px', textAlign: 'left', color: '#a0a9c1', fontFamily: 'Georgia, serif' }}>Catalyst</th>
                  <th style={{ padding: '12px', textAlign: 'right', color: '#a0a9c1', fontFamily: 'Georgia, serif' }}>P&L</th>
                  <th style={{ padding: '12px', textAlign: 'center', color: '#a0a9c1', fontFamily: 'Georgia, serif' }}>Outcome</th>
                </tr>
              </thead>
              <tbody>
                {currentWeek.trades.map((trade, index) => (
                  <tr key={index} style={{ borderBottom: '1px solid #3a4a6b' }}>
                    <td style={{ padding: '12px', color: '#ffffff', fontFamily: 'Georgia, serif', fontWeight: 'bold' }}>
                      {trade.ticker}
                    </td>
                    <td style={{ padding: '12px', color: '#5BC0DE', fontFamily: 'Georgia, serif' }}>
                      {trade.strategy}
                    </td>
                    <td style={{ padding: '12px', color: '#ffffff', fontFamily: 'Georgia, serif' }}>
                      {trade.catalyst}
                    </td>
                    <td style={{
                      padding: '12px',
                      textAlign: 'right',
                      color: trade.pnl > 0 ? '#00FF5F' : '#FF0000',
                      fontFamily: 'Georgia, serif',
                      fontWeight: 'bold'
                    }}>
                      {formatCurrency(trade.pnl)}
                    </td>
                    <td style={{ padding: '12px', textAlign: 'center' }}>
                      <span style={{
                        backgroundColor: trade.outcome === 'Win' ? '#00FF5F' : '#FF0000',
                        color: '#ffffff',
                        padding: '4px 8px',
                        borderRadius: '4px',
                        fontSize: '12px',
                        fontWeight: 'bold',
                        fontFamily: 'Georgia, serif'
                      }}>
                        {trade.outcome}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PortfolioAnalysis;